import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { SitePage } from "@tih/shared";
import { ApiError, fetchPage } from "../api/client";
import { renderMarkdown } from "../lib/markdown";

export function SitePageView() {
  const { slug = "" } = useParams();
  const [page, setPage] = useState<SitePage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setError(null);
    setNotFound(false);
    fetchPage(slug)
      .then(setPage)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setError(String(e?.message ?? e));
      });
  }, [slug]);

  const html = useMemo(() => {
    if (!page) return "";
    if (page.bodyFormat === "BLOCKS") return page.body;
    return renderMarkdown(page.body);
  }, [page]);

  if (notFound) {
    return (
      <section className="section" style={{ textAlign: "center", paddingTop: 160 }}>
        <h2 className="sec-title">Page not found</h2>
        <p style={{ opacity: 0.75, marginBottom: 32 }}>That page may have been moved or unpublished.</p>
        <Link to="/" className="btn btn-primary">Back home</Link>
      </section>
    );
  }

  if (error) return <section className="section"><div className="form-error">{error}</div></section>;
  if (!page) return <section className="section"><div style={{ opacity: 0.5 }}>Loading…</div></section>;

  return (
    <article className="section blog-post">
      {page.coverUrl && (
        <img
          src={page.coverUrl}
          alt=""
          className="blog-post-cover"
          style={{ width: "100%", borderRadius: 8, marginBottom: 32 }}
        />
      )}
      <h1 className="blog-post-title">{page.title}</h1>
      {page.excerpt && <p className="blog-post-excerpt">{page.excerpt}</p>}
      <div className="blog-post-body" dangerouslySetInnerHTML={{ __html: html }} />
    </article>
  );
}
